import React, { useState } from 'react';
import { 
  IconButton, 
  Tooltip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Box,
  Chip
} from '@mui/material';
import { 
  PersonOff as DeactivateIcon, 
  Person as ActivateIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { fetchWithToken } from 'helpers/fetch';
import { endPoints } from 'const/endPoints';
import Toast from 'components/common/Popup/Toast';

const UserStatusToggle = ({ user, flag }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const isActive = user.isActive !== false; // Default to true if undefined
  const isAdmin = user.role === 'admin';

  const handleToggleStatus = async () => {
    setLoading(true);
    try {
      const endpoint = isActive 
        ? `${endPoints.deactivate_user}/${user.id}`
        : `${endPoints.reactivate_user}/${user.id}`;
      
      const response = await fetchWithToken(endpoint, {}, 'PUT');
      const data = await response.json();
      
      if (response.status === 200) {
        Toast('success', data.message);
        flag(prev => !prev); // Update parent component
        setOpenDialog(false);
      } else {
        Toast('error', data.message || 'Error al cambiar el estado del usuario');
      }
    } catch (error) {
      Toast('error', 'Error al cambiar el estado del usuario');
    }
    setLoading(false);
  };

  // No mostrar botón para administradores
  if (isAdmin) {
    return (
      <Chip
        label="Admin"
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontSize: '0.7rem' }}
      />
    );
  }

  return (
    <>
      <Tooltip 
        title={isActive ? "Desactivar usuario" : "Reactivar usuario"}
      >
        <IconButton
          onClick={() => setOpenDialog(true)}
          color={isActive ? "error" : "success"}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: isActive ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
            }
          }}
        >
          {isActive ? <DeactivateIcon /> : <ActivateIcon />}
        </IconButton>
      </Tooltip>

      <Dialog
        open={openDialog}
        onClose={() => !loading && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color={isActive ? "error" : "success"} />
            {isActive ? "Desactivar Usuario" : "Reactivar Usuario"}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {isActive 
              ? `¿Está seguro que desea desactivar al usuario ${user.firstName} ${user.lastName}?`
              : `¿Está seguro que desea reactivar al usuario ${user.firstName} ${user.lastName}?`
            }
          </Typography>
          
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Usuario:</strong> {user.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Estado actual:</strong> 
              <Chip
                label={isActive ? "Activo" : "Inactivo"}
                size="small"
                color={isActive ? "success" : "error"}
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            {isActive 
              ? "⚠️ Los usuarios desactivados no podrán acceder a sus cursos ni realizar pagos."
              : "✅ Los usuarios reactivados podrán acceder nuevamente a sus cursos y funcionalidades."
            }
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)} 
            disabled={loading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleToggleStatus}
            disabled={loading}
            color={isActive ? "error" : "success"}
            variant="contained"
            sx={{
              minWidth: 120,
            }}
          >
            {loading 
              ? "Procesando..." 
              : isActive 
                ? "Desactivar" 
                : "Reactivar"
            }
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserStatusToggle;